import { UniqueIdentifier } from "@dnd-kit/core";
import { ReturnsAndClaimsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  NEEDS_ATTENTION_FOLDER_NAME,
  ReturnSystemView,
} from "@redotech/redo-return-operations-common/views/default-return-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import {
  TreeItem,
  TreeItemType,
  TreeViewItem,
} from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo } from "react";
import { useMatch } from "react-router-dom";
import { useGiftReceiptRequestsCount } from "../../../../gift-receipt-requests/use-gift-receipt-requests-count";
import {
  OrderManagementSuiteStatusesContext,
  RETURNS_ICON,
  RETURNS_ICON_FILLED,
} from "../../../../redo-suite/order-management-cloud";
import { ReturnViewOrderingContext } from "../../../../services/returns/returns-view-ordering-service";
import { useViewFolderLogic } from "./use-view-folder-logic";
import {
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

export const ReturnsItem = memo(function ReturnsItem() {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { returnsEnabled } = useContext(OrderManagementSuiteStatusesContext);
  const urlPrefix = routes.returnsHref;
  const returnsInstantExchangeEnabled = team.settings.exchanges.allowInstant;

  const hasPermissionToViewItem = useHasPermission(
    ReturnsAndClaimsPermissions.VIEW_RETURN,
  );

  const returnViewOrderingContext = useRequiredContext(
    ReturnViewOrderingContext,
  );
  const { notificationCounts } = returnViewOrderingContext;

  const allViews = useMemo(() => {
    return [...returnViewOrderingContext.views].filter(
      (view) => view.name !== ReturnSystemView.CREATE,
    );
  }, [returnViewOrderingContext]);

  const allFolders = useMemo(() => {
    return [...returnViewOrderingContext.folders];
  }, [returnViewOrderingContext]);

  const {
    viewsWithoutFolders,
    foldersWithViews,
    folderNotificationCounts,
    totalNotificationCount,
  } = useViewFolderLogic<"returns">({
    allViews,
    allFolders,
    notificationCounts,
  });

  const giftReceiptsEnabled = !!team.settings.returns?.enableGiftReceipts;

  const giftReceiptRequestsCount = useGiftReceiptRequestsCount();
  const giftReceiptRequestsNotificationCount =
    giftReceiptRequestsCount !== undefined && giftReceiptRequestsCount > 0
      ? giftReceiptRequestsCount
      : undefined;

  const { sortableItems, needsAttentionFolderExists } = useMemo(() => {
    const items = viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: notificationCounts,
      folderCounts: folderNotificationCounts,
      hideZeroNotifications: false,
    });

    const needsAttentionFolder = items.find(
      (item) =>
        item.type === TreeItemType.Folder &&
        item.name === NEEDS_ATTENTION_FOLDER_NAME,
    );

    if (
      !needsAttentionFolder ||
      needsAttentionFolder.type !== TreeItemType.Folder
    ) {
      return { sortableItems: items, needsAttentionFolderExists: false };
    }

    const extraChildren: TreeViewItem[] = [];

    if (returnsInstantExchangeEnabled) {
      extraChildren.push({
        id: "returns-instant-exchange",
        name: "Instant exchange",
        friendlyName: "Instant exchange",
        type: TreeItemType.View,
        actionHref: routes.getReturnsSubRoute("instant-exchange"),
      });
    }

    if (giftReceiptsEnabled) {
      extraChildren.push({
        id: "gift-receipt-requests",
        name: "Gift receipt requests",
        friendlyName: "Gift receipt requests",
        type: TreeItemType.View,
        actionHref: routes.getReturnsSubRoute("gift-receipt-requests"),
        notificationCount: giftReceiptRequestsNotificationCount,
      });
    }

    needsAttentionFolder.children = [
      ...needsAttentionFolder.children,
      ...extraChildren,
    ];

    return { sortableItems: items, needsAttentionFolderExists: true };
  }, [
    viewsWithoutFolders,
    foldersWithViews,
    urlPrefix,
    notificationCounts,
    folderNotificationCounts,
    returnsInstantExchangeEnabled,
    giftReceiptsEnabled,
    giftReceiptRequestsNotificationCount,
    routes,
  ]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    returnViewOrderingContext.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const { views, folders } = treeToViewsAndFolders<"returns">(
      flattenedItems,
      allViews,
      allFolders,
    );
    returnViewOrderingContext.updateOrdering(views, folders);
  });

  const inmarIntegrationLoad = useLoad(async () => {
    return await rpcClient.getInmarIntegration({});
  }, []);

  const anyChildSelected = useMatch(`${urlPrefix}/*`);

  if (!returnsEnabled || !hasPermissionToViewItem) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: urlPrefix }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={RETURNS_ICON_FILLED}
      friendlyName="Returns"
      id="returns"
      notificationCount={totalNotificationCount}
      UnfilledIcon={RETURNS_ICON}
    >
      <AllReturnsItem />
      <GiftReceiptRequestsItem
        hidden={!giftReceiptsEnabled || needsAttentionFolderExists}
        notificationCount={giftReceiptRequestsNotificationCount}
      />
      <InmarBlindReturnsItem
        hidden={!inmarIntegrationLoad.value?.inmarIntegration?.enabled}
      />
      <InstantExchangeItem
        hidden={!returnsInstantExchangeEnabled || needsAttentionFolderExists}
      />
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <ReturnsAddViewItem />
      <ReturnsCreateFolderItem />
    </RedoSecondaryNavItem>
  );
});

const AllReturnsItem = memo(function AllReturnsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getReturnsSubRoute("all-returns") }}
      friendlyName="All returns"
      id="all-returns"
    />
  );
});

const GiftReceiptRequestsItem = memo(function GiftReceiptRequestsItem({
  hidden,
  notificationCount,
}: {
  hidden: boolean;
  notificationCount?: number;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getReturnsSubRoute("gift-receipt-requests"),
      }}
      friendlyName="Gift receipt requests"
      hidden={hidden}
      id="gift-receipt-requests"
      notificationCount={notificationCount}
    />
  );
});

const InmarBlindReturnsItem = memo(function InmarBlindReturnsItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getReturnsSubRoute("inmar-blind-returns"),
      }}
      friendlyName="Inmar blind returns"
      hidden={hidden}
      id="inmar-blind-returns"
    />
  );
});

const InstantExchangeItem = memo(function InstantExchangeItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getReturnsSubRoute("instant-exchange"),
      }}
      friendlyName="Instant exchange"
      hidden={hidden}
      id="returns-instant-exchange"
    />
  );
});

const ReturnsAddViewItem = memo(function ReturnsAddViewItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getReturnsSubRoute("create") }}
      friendlyName="+ Add new view"
      id="orders-returns-create"
    />
  );
});

const ReturnsCreateFolderItem = memo(function ReturnsCreateFolderItem() {
  const returnViewOrderingContext = useRequiredContext(
    ReturnViewOrderingContext,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "button",
        onClick: () => {
          returnViewOrderingContext.openCreateFolderModal();
        },
      }}
      friendlyName="+ Add new folder"
      id="orders-returns-create-folder"
    />
  );
});
